import { BaseNode, BaseNodeProps } from "./BaseNode";
import { Handle, Position } from "@xyflow/react";
import { Compass } from "@quake/shared-ui/components/animate-ui/icons/compass";
import { AnimateIcon } from "@quake/shared-ui/components/animate-ui/icons/icon";
import CheckCircle2Icon from "lucide-react/dist/esm/icons/check-circle-2";
import XCircleIcon from "lucide-react/dist/esm/icons/x-circle";
import { useWorkflowBuilderStore } from "../store/useWorkflowBuilderStore";

type ConditionNodeProps = BaseNodeProps & {
  readOnly: boolean;
  data: {
    label: string;
    readableId: string;
    expression?: string;
  };
  id: string;
};

export function ConditionNode({
  data,
  onAddAction,
  onEdit,
  ...props
}: ConditionNodeProps & { onAddAction?: ({ edgeColor }: { edgeColor?: string }) => void; onEdit?: () => void }) {
  const direction = useWorkflowBuilderStore((state) => state.meta?.direction);
  return (
    <BaseNode
      className="px-1"
      color="bg-linear-to-r from-purple-500 to-purple-600 border border-purple-400 dark:bg-slate-800"
      data={{ ...data, label: data.label || "Condition" }}
      icon={
        <AnimateIcon animateOnHover>
          <Compass />
        </AnimateIcon>
      }
      isSourceConnectable={false}
      isTargetConnectable
      onAddAction={onAddAction}
      onEdit={onEdit}
      showAddAction={false}
      {...props}
    >
      <Handle
        id="truthy"
        type="source"
        position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}
        className="bg-green-500! cursor-pointer!"
        style={{
          top: direction === "top-to-bottom" ? 47 : 15,
          height: "15px",
          width: "auto",
          lineHeight: "1",
          fontSize: "10px",
          transform: direction === "top-to-bottom" ? "translate(26%, -50%) rotate(90deg)" : "translate(102%, -50%)",
          borderRadius: "0",
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
          borderLeft: "none",
          pointerEvents: props.readOnly ? "none" : "auto",
        }}
        onMouseDownCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={() => onAddAction?.({ edgeColor: "truthy" })}
      >
        <CheckCircle2Icon
          className="w-full h-full"
          style={{ transform: direction === "top-to-bottom" ? "rotate(-90deg)" : "rotate(0deg)" }}
        />
      </Handle>
      <Handle
        id="falsy"
        type="source"
        position={direction === "top-to-bottom" ? Position.Bottom : Position.Right}
        className="bg-red-500!"
        style={{
          top: direction === "top-to-bottom" ? 47 : 30,
          height: "15px",
          width: "auto",
          lineHeight: "1",
          fontSize: "10px",
          transform: direction === "top-to-bottom" ? "translate(-122%,-48%)  rotate(90deg)" : "translate(102%, -50%)",
          borderRadius: "0",
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
          borderLeft: "none",
          pointerEvents: props.readOnly ? "none" : "auto",
        }}
        onMouseDownCapture={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={() => onAddAction?.({ edgeColor: "falsy" })}
      >
        <XCircleIcon className="w-full h-full" />
      </Handle>
      {data.output && (
        <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1 cursor-pointer" onClick={onEdit}>
          <CheckCircle2Icon className="w-4 h-4 text-white" />
        </div>
      )}
      {data.output === false && (
        <div className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 cursor-pointer" onClick={onEdit}>
          <XCircleIcon className="w-4 h-4 text-white" />
        </div>
      )}
    </BaseNode>
  );
}
